import type { HTMLAttributes, ReactNode } from 'react';

/**
 * Shared card surface.
 * - `default` — white background, rounded 8px corners, soft shadow.
 * - `bordered` — slightly tighter, bordered variant used for plot boxes.
 *
 * The `actions` slot + `href`/`hrefLabel` render a "Know more" anchor.
 */

export type CardVariant = 'default' | 'elevated' | 'bordered' | 'flat';

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  variant?: CardVariant;
  title?: ReactNode;
  subtitle?: ReactNode;
  /** Optional right-aligned slot for controls (e.g. period selector). */
  actions?: ReactNode;
  href?: string;
  hrefLabel?: ReactNode;
  children?: ReactNode;
}

// bg white, rounded 8px, soft drop shadow, padded.
const CARD_BASE =
  'bg-white rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.1)] overflow-hidden';

const CARD_VARIANTS: Record<CardVariant, string> = {
  default: '',
  // Slightly stronger shadow for emphasis.
  elevated: 'shadow-[0_2px_6px_rgba(0,0,0,0.15)]',
  // Bordered variant for plot boxes.
  bordered: 'border border-[color:var(--gray-200)] shadow-[0_1px_3px_rgba(0,0,0,0.1)]',
  // Faded variant for background-only surfaces.
  flat: 'bg-[color:var(--gray-50)] shadow-none',
};

export function Card({
  variant = 'default',
  className,
  title,
  subtitle,
  actions,
  href,
  hrefLabel,
  children,
  ...rest
}: CardProps): JSX.Element {
  const classes = [CARD_BASE, CARD_VARIANTS[variant], className].filter(Boolean).join(' ');
  const hasHeader = Boolean(title || subtitle || actions || href);
  return (
    <div className={classes} {...rest}>
      {hasHeader ? (
        <div className="flex items-start justify-between gap-3 px-4 pt-3">
          <div>
            {title ? (
              <h3 className="font-[var(--font-heading)] text-[1rem] font-semibold text-[color:var(--text-dark)] m-0">
                {title}
              </h3>
            ) : null}
            {subtitle ? (
              <p className="text-xs text-[color:var(--gray-500)] mt-1 m-0">{subtitle}</p>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {actions}
            {href ? (
              <a
                href={href}
                className="inline-block bg-[color:var(--accent)] text-[color:var(--text-light)] no-underline text-xs font-medium px-3 py-1 rounded-[15px] shadow-[0_1px_3px_rgba(0,0,0,0.1)] hover:bg-[color:var(--accent-hover)] transition-colors"
              >
                {hrefLabel ?? 'Know more'}
              </a>
            ) : null}
          </div>
        </div>
      ) : null}
      {children}
    </div>
  );
}

export interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
}

export function CardBody({ className, children, ...rest }: CardBodyProps): JSX.Element {
  const classes = ['p-4', className].filter(Boolean).join(' ');
  return (
    <div className={classes} {...rest}>
      {children}
    </div>
  );
}

export default Card;
